import {
  HistogramSeries,
  LineSeries,
  LineStyle,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type SeriesType,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Point, RenderedIndicator, RenderedOutput } from './instances';

interface Held {
  series: ISeriesApi<SeriesType>;
  points: Point[];
  lines: IPriceLine[];
}

/** Everything about how an indicator looks, so a change to it (not to its data) rebuilds its series. */
const structureOf = (list: RenderedIndicator[]): string =>
  JSON.stringify(
    list.map((i) => [i.uid, i.overlay, i.levels, i.outputs.map((o) => [o.key, o.style, o.color, o.width])]),
  );

/** How many trailing points differ between two point lists; null when something before the last point changed. */
export function tailChange(previous: Point[], next: Point[]): number | null {
  if (next.length < previous.length) return null;
  const limit = previous.length - 1; // only the last existing point may change
  for (let i = 0; i < limit; i++) {
    const a = previous[i]!;
    const b = next[i]!;
    if (a.time !== b.time || a.value !== b.value || a.color !== b.color) return null;
  }
  if (previous.length > 0 && previous[limit]!.time !== next[limit]!.time) return null;
  return next.length - limit;
}

/**
 * Draws indicators on a chart: trend indicators on the price pane, everything else in panes of their own below it (one
 * per indicator, in the order they were added). Structure changes rebuild the series; new data is applied incrementally
 * when only the live bar moved, and with `setData` otherwise.
 */
export class IndicatorLayer {
  private held = new Map<string, Held>(); // `${uid}:${outputKey}`
  private structure = '';
  private mutedColor = '#888';

  constructor(private readonly chart: IChartApi) {}

  destroy() {
    this.clear();
  }

  private clear() {
    for (const { series } of this.held.values()) {
      try {
        this.chart.removeSeries(series);
      } catch {
        // the chart may already be gone
      }
    }
    this.held.clear();
  }

  update(list: RenderedIndicator[], mutedColor: string) {
    this.mutedColor = mutedColor;
    const structure = structureOf(list);
    if (structure !== this.structure) {
      this.clear();
      this.build(list);
      this.structure = structure;
    }
    for (const indicator of list) {
      for (const output of indicator.outputs) this.apply(indicator.uid, output);
    }
    this.sizePanes(list);
  }

  private paneOf(list: RenderedIndicator[], indicator: RenderedIndicator): number {
    if (indicator.overlay) return 0;
    return 1 + list.filter((i) => !i.overlay).findIndex((i) => i.uid === indicator.uid);
  }

  private build(list: RenderedIndicator[]) {
    for (const indicator of list) {
      const pane = this.paneOf(list, indicator);
      let first: ISeriesApi<SeriesType> | null = null;
      for (const output of indicator.outputs) {
        const series = this.addSeries(output, pane);
        first ??= series;
        this.held.set(`${indicator.uid}:${output.key}`, { series, points: [], lines: [] });
      }
      if (first && !indicator.overlay) {
        const owner = this.held.get(`${indicator.uid}:${indicator.outputs[0]!.key}`)!;
        owner.lines = indicator.levels.map((price) =>
          first.createPriceLine({
            price,
            color: this.mutedColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dotted,
            axisLabelVisible: false,
            title: '',
          }),
        );
      }
    }
  }

  private addSeries(output: RenderedOutput, pane: number): ISeriesApi<SeriesType> {
    const common = { priceLineVisible: false, lastValueVisible: pane !== 0, title: '' };
    if (output.style === 'histogram') {
      return this.chart.addSeries(HistogramSeries, { ...common, color: output.color, base: 0 }, pane);
    }
    return this.chart.addSeries(
      LineSeries,
      {
        ...common,
        color: output.color,
        lineWidth: output.width,
        // Parabolic SAR is a row of dots, not a line.
        lineVisible: output.style !== 'dots',
        pointMarkersVisible: output.style === 'dots',
        pointMarkersRadius: 2,
        crosshairMarkerVisible: false,
      },
      pane,
    );
  }

  private apply(uid: string, output: RenderedOutput) {
    const held = this.held.get(`${uid}:${output.key}`);
    if (!held) return;
    const points = output.points;
    const change = held.points.length === 0 ? null : tailChange(held.points, points);
    const data = (p: Point) =>
      p.color === undefined
        ? { time: p.time as UTCTimestamp, value: p.value }
        : { time: p.time as UTCTimestamp, value: p.value, color: p.color };
    if (change === null || change > 2) {
      held.series.setData(points.map(data) as never);
    } else if (change > 0) {
      for (let i = points.length - change; i < points.length; i++)
        held.series.update(data(points[i]!) as never);
    }
    held.points = points;
  }

  private sizePanes(list: RenderedIndicator[]) {
    const panes = this.chart.panes();
    const below = list.filter((i) => !i.overlay).length;
    if (panes.length <= 1) return;
    panes[0]?.setStretchFactor(below > 0 ? 3 : 1);
    for (let i = 1; i < panes.length; i++) panes[i]?.setStretchFactor(1);
  }
}
